import { useCallback, useEffect, useState } from 'react'
import { useEnsureChain } from '../../../hooks/useEnsureChain'
import './NetworkGuard.scss'

/** Matches the rule in NetworkGuard.scss that reserves the bar's height. */
const BODY_CLASS = 'has-network-guard'

/**
 * App-wide "wrong network" banner.
 *
 * The app talks to exactly one chain. On any other chain every read hook is
 * gated off (`enabled: isCorrectChain` in usePolygonContracts.js) and every
 * signature throws — so the screen fills with zeros and a wagmi error that names
 * neither chain. Before this banner there was nothing telling the user why.
 *
 * Deliberately not dismissible: it is the explanation for a dashboard showing
 * 0.00 everywhere, and hiding it leaves the user with a silently broken app.
 * It is the SAME `useEnsureChain` the login and write paths use, so what the
 * banner says and what those paths enforce cannot drift apart.
 */
const NetworkGuard = () => {
  const { ensureChain, isWrongChain, isSwitching, chainName, currentChainName } =
    useEnsureChain()
  const [refused, setRefused] = useState(false)

  const handleSwitch = useCallback(async () => {
    setRefused(false)
    const ok = await ensureChain()
    // `isWrongChain` clears on its own when the switch lands; this only drives
    // the "we asked and it didn't happen" hint.
    setRefused(!ok)
  }, [ensureChain])

  // The bar is fixed to the bottom, so without this it would sit on top of
  // whatever the page ends with — a footer link, or the action button on a short
  // screen. Cleaned up on unmount so the padding never outlives the bar.
  useEffect(() => {
    if (!isWrongChain) return undefined
    document.body.classList.add(BODY_CLASS)
    return () => document.body.classList.remove(BODY_CLASS)
  }, [isWrongChain])

  if (!isWrongChain) return null

  return (
    <div className="network_guard" role="alert">
      <div className="network_guard__body">
        <span className="network_guard__icon" aria-hidden="true">
          !
        </span>
        <div className="network_guard__text">
          <strong>Wrong network</strong>
          <span>
            {`FRDM runs on ${chainName}.`}
            {currentChainName
              ? ` Your wallet is on ${currentChainName}.`
              : ' Your wallet is on a different network.'}
          </span>
          {refused && !isSwitching && (
            <span className="network_guard__hint">
              {`The switch didn't go through. Approve it in your wallet, or switch to ${chainName} there manually.`}
            </span>
          )}
        </div>
      </div>

      <button
        type="button"
        className="network_guard__button"
        onClick={handleSwitch}
        disabled={isSwitching}
      >
        {isSwitching ? 'Switching…' : `Switch to ${chainName}`}
      </button>
    </div>
  )
}

export default NetworkGuard
